import styled, { css } from 'styled-components';
import { px2rem, inPixels, getColorMixValues } from '../utils/helpers/style.js';
import { styles } from '../constants/styles.js';
import { spacingNumerical } from '../constants/spacing.js';
import { contentTextStyles } from '../ContentText/styles.web.js';
import { boxStyles } from '../Box/styles.js';
import { boxStyledOptions } from '../utils/helpers/styled.js';
import { generateColorLuminances } from '../utils/helpers/generateColorLuminances.js';

var accordionBorderMapping = {
  top: ['full'],
  left: ['full'],
  right: ['full'],
  bottom: ['full', 'bottomOnly']
};
var hasBorder = function hasBorder(border) {
  return function (props) {
    return props.$opened || accordionBorderMapping[border].includes(props.$borderStyle);
  };
};
var resolveBorderRemoval = function resolveBorderRemoval(border) {
  return function (props) {
    return hasBorder(border)(props) ? null : 0;
  };
};
var buttonStyles = function buttonStyles(props) {
  var plainLuminances = generateColorLuminances('hover', props.theme, {
    color: props.$color
  });
  return props.$status === 'plain' ? css(["color:", ";padding:0 ", " 0 0;width:auto;", "{right:0;}.fill{fill:", ";}&:hover{color:", ";.fill{fill:", ";}}"], props.$focused ? plainLuminances.bg : props.$color, inPixels(spacingNumerical.m), iconContainer, props.$color, plainLuminances.bg, plainLuminances.bg) : css(["color:", ";padding:0 ", " 0 ", ";width:100%;", "{right:", ";}"], props.$textColor || props.theme.accordion.web.fontColor, inPixels(54), function (p) {
    return p.$borderStyle === 'full' ? p.theme.accordion.web.content.paddingHorizontal : p.theme.accordion.web.content.borderless.paddingHorizontal;
  }, iconContainer, function (p) {
    return p.$borderStyle === 'full' ? p.theme.accordion.web.content.paddingHorizontal : p.theme.accordion.web.content.borderless.paddingHorizontal;
  });
};
var getAccordionHoverStyle = function getAccordionHoverStyle(props) {
  var textColors = [props.theme.accordion.web.fontColor, props.theme.colors.background];
  var _getColorMixValues = getColorMixValues(textColors, props.theme.accordion.web.backgroundColor, props.theme.colors.primary),
    fg = _getColorMixValues.fg,
    bg = _getColorMixValues.bg;
  return css(["color:", ";background:", ";transition:background ", ";transition-timing-function:", ";"], fg, bg, styles.transition.timing.fastest, styles.transition["function"].base);
};
var iconContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__iconContainer",
  componentId: "sc-1uebnti-0"
})(["display:inline-flex;position:absolute;top:50%;transform:translateY(-50%);"]);
var button = /*#__PURE__*/styled.button.withConfig({
  displayName: "styles__button",
  componentId: "sc-1uebnti-1"
})(["", ";", ";outline:none;text-align:left;background:transparent;border:none;border-radius:", ";border-bottom-left-radius:", ";border-bottom-right-radius:", ";cursor:pointer;display:block;font-size:", ";min-height:", ";line-height:1;position:relative;transition:border-radius,fill ", ";transition-timing-function:", ";&:hover{", "}"], buttonStyles, contentTextStyles, function (props) {
  return px2rem(props.theme.accordion.web.borders.radius);
}, function (props) {
  return props.$borderStyle === 'none' && props.$opened ? 0 : undefined;
}, function (props) {
  return props.$borderStyle === 'none' && props.$opened ? 0 : undefined;
}, function (p) {
  return p.theme.accordion.web.sizings[p.$size].fontSize;
}, function (p) {
  return p.theme.accordion.web.sizings[p.$size].height;
}, styles.transition.timing.fastest, styles.transition["function"].base, function (p) {
  return p.$borderStyle === 'none' && p.$status !== 'plain' && getAccordionHoverStyle(p);
});
var content = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__content",
  componentId: "sc-1uebnti-2"
})(["overflow-y:", ";transition:max-height ", ";transition-timing-function:", ";max-height:", ";&::-webkit-scrollbar{background:", ";width:", ";}&::-webkit-scrollbar-track{background:", ";box-shadow:none;}&::-webkit-scrollbar-thumb{background-color:", ";border:", " solid ", ";box-shadow:none;border-radius:", ";}"], function (props) {
  return props.$overflow ? props.$overflow : 'hidden';
}, styles.transition.timing.slowest, styles.transition["function"].base, function (props) {
  return props.$maxHeight;
}, function (props) {
  return props.theme.accordion.web.backgroundColor;
}, px2rem(8), function (props) {
  return props.theme.accordion.web.backgroundColor;
}, function (props) {
  return props.theme.accordion.web.borders.inactive;
}, px2rem(2), function (props) {
  return props.theme.accordion.web.backgroundColor;
}, function (p) {
  return p.theme.borders.radius;
});
var inner = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__inner",
  componentId: "sc-1uebnti-3"
})(["", ";"], function (props) {
  return props.$status === 'plain' ? css(["padding:", " 0;padding-top:", ";"], props.theme.accordion.web.sizings[props.$size].content.paddingVertical, props.theme.accordion.web.sizings[props.$size].content.paddingTop) : css(["padding:", " ", ";padding-top:", ";"], props.theme.accordion.web.sizings[props.$size].content.paddingVertical, props.$borderStyle === 'full' ? props.theme.accordion.web.content.paddingHorizontal : props.theme.accordion.web.content.borderless.paddingHorizontal, props.theme.accordion.web.sizings[props.$size].content.paddingTop);
});
var isFocusBorderHidden = function isFocusBorderHidden(props) {
  return props.$borderStyle === 'none';
};
var wrapper = /*#__PURE__*/styled.div.withConfig(boxStyledOptions).withConfig({
  displayName: "styles__wrapper",
  componentId: "sc-1uebnti-4"
})(["background:", ";border-style:solid;border-color:", ";transition:border-color,border-radius ", ";transition-timing-function:", ";border-width:", ";border-top-width:", ";border-left-width:", ";border-bottom-width:", ";border-right-width:", ";padding-top:", ";padding-left:", ";padding-right:", ";border-radius:", ";border-bottom-left-radius:", ";border-bottom-right-radius:", ";&:hover{border-color:", ";padding-top:", ";padding-left:", ";padding-right:", ";border-top-width:", ";border-left-width:", ";border-right-width:", ";border-bottom-width:", ";border-bottom-left-radius:", ";border-bottom-right-radius:", ";", "::-webkit-scrollbar-thumb{background-color:", ";}}&:has(", ":focus-visible){outline-offset:0;box-shadow:0 0 0 4px ", ";outline:2px solid ", ";border-color:", ";padding-top:", ";padding-left:", ";padding-right:", ";border-top-width:", ";border-left-width:", ";border-right-width:", ";border-bottom-width:", ";border-bottom-left-radius:", ";border-bottom-right-radius:", ";}position:relative;box-sizing:border-box;", ""], function (p) {
  return p.$status !== 'plain' ? p.theme.accordion.web.backgroundColor : undefined;
}, function (p) {
  if (p.$focused) {
    return p.theme.accordion.web.borders.active;
  }
  return p.theme.accordion.web.borders.inactive;
}, styles.transition.timing.fastest, styles.transition["function"].base, function (props) {
  return props.$borderStyle === 'none' ? 0 : inPixels(2);
}, resolveBorderRemoval('top'), resolveBorderRemoval('left'), resolveBorderRemoval('bottom'), resolveBorderRemoval('right'), function (props) {
  return hasBorder('top')(props) || isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return hasBorder('left')(props) || isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return hasBorder('right')(props) || isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return px2rem(props.theme.accordion.web.borders.radius);
}, function (props) {
  return props.$borderStyle === 'bottomOnly' && !props.$opened ? 0 : px2rem(props.theme.accordion.web.borders.radius);
}, function (props) {
  return props.$borderStyle === 'bottomOnly' && !props.$opened ? 0 : px2rem(props.theme.accordion.web.borders.radius);
}, function (p) {
  return p.theme.accordion.web.borders.active;
}, function (props) {
  return isFocusBorderHidden(props) ? undefined : 0;
}, function (props) {
  return isFocusBorderHidden(props) ? undefined : 0;
}, function (props) {
  return isFocusBorderHidden(props) ? undefined : 0;
}, function (props) {
  return isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return px2rem(props.theme.accordion.web.borders.radius);
}, function (props) {
  return px2rem(props.theme.accordion.web.borders.radius);
}, content, function (props) {
  return props.theme.accordion.web.borders.active;
}, button, function (p) {
  return p.theme.tokens.focus.primary;
}, function (p) {
  return p.theme.tokens.focus.secondary;
}, function (p) {
  return p.theme.accordion.web.borders.active;
}, function (props) {
  return isFocusBorderHidden(props) ? undefined : 0;
}, function (props) {
  return isFocusBorderHidden(props) ? undefined : 0;
}, function (props) {
  return isFocusBorderHidden(props) ? undefined : 0;
}, function (props) {
  return isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return isFocusBorderHidden(props) ? 0 : inPixels(2);
}, function (props) {
  return px2rem(props.theme.accordion.web.borders.radius);
}, function (props) {
  return px2rem(props.theme.accordion.web.borders.radius);
}, boxStyles);

export { button, content, iconContainer, inner, wrapper };
